import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  Flex,
  FlexColumn,
  ToggleButton,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup, Typography } from '@mui/material';
import Ajv, { ErrorObject, ValidateFunction } from 'ajv';
import YAML, { YAMLException } from 'js-yaml';
import YamlCodeEditor from './YamlCodeEditor';
import YamlForm from './YamlForm';
import { schemaToFormFields } from './schemaToFormFields';
import {
  dumpYamlFormValues,
  parseYamlFormValues,
  removeLineComments,
} from './yamlFormValues';
import {
  EditorMode,
  EMPTY_YAML_FORM_RESOURCE_OPTIONS,
  isJsonSchema,
  JsonSchema,
  YamlFormResourceOptions,
  YamlFormValues,
  YamlValidationError,
} from './types';

const EditorRoot = styled(FlexColumn)`
  flex: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 0;
  overflow: auto;
`;

const ToggleRow = styled(Flex)`
  justify-content: flex-end;
  width: 100%;
  max-width: 960px;
  margin-inline: auto;
  padding-right: ${({ theme }) => theme.spacing(4)};
`;

const ajv = new Ajv({ allErrors: true });

const getSchema = (
  testName: string | undefined,
  schema: object | undefined,
) => {
  if (!schema || !testName) {
    return undefined;
  }
  return {
    type: 'array',
    items: {
      type: 'object',
      properties: {
        [testName]: schema,
      },
      required: [testName],
      additionalProperties: false,
    },
    minItems: 1,
    maxItems: 1,
  };
};

const findParamLine = (lines: string[], paramPath: string[]) => {
  let index = 0;
  for (const param of paramPath) {
    const relativeIndex = lines
      .slice(index + 1)
      .findIndex((l) => l.includes(`${param}:`));
    if (relativeIndex === -1) {
      return 0;
    }
    index += relativeIndex + 1;
  }
  return index;
};

const jsonSchemaErrorsToValidationErrors = (
  yaml: string,
  errors: ErrorObject[],
): YamlValidationError[] => {
  const lines = yaml.split('\n');
  return errors.map((error) => {
    const pathParts = error.instancePath.split('/').slice(1);
    const paramName = pathParts[pathParts.length - 1];
    const paramStr = paramName ? `Parameter "${paramName}"` : 'Configuration';
    const line = findParamLine(lines, pathParts);
    let message = error.message ?? '';
    if (
      error.params &&
      'allowedValues' in error.params &&
      Array.isArray(error.params.allowedValues)
    ) {
      message = `${message} (${error.params.allowedValues.join(', ')})`;
    }
    if (
      error.params &&
      'additionalProperty' in error.params &&
      typeof error.params.additionalProperty === 'string'
    ) {
      message = `${message} (${error.params.additionalProperty})`;
    }
    return {
      line,
      message: `${paramStr}: ${message}`,
    };
  });
};

const validateYaml = (
  value: string,
  validator: ValidateFunction | undefined,
): YamlValidationError[] => {
  try {
    const parsed = YAML.load(value);
    if (!validator) {
      return [];
    }
    validator(parsed);
    return jsonSchemaErrorsToValidationErrors(value, validator.errors || []);
  } catch (e) {
    if (!(e instanceof YAMLException)) {
      return [];
    }
    return [
      {
        line: e.mark.line,
        message: `Invalid YAML: ${e.reason}`,
      },
    ];
  }
};

type Props = {
  value: string;
  onChange: (yaml: string, isValid: boolean) => void;
  configSchema?: object;
  testName?: string;
  readOnly?: boolean;
  grayedOut?: boolean;
  isPending?: boolean;
  resourceOptions?: YamlFormResourceOptions;
};

const YamlEditor = ({
  value,
  onChange,
  configSchema,
  testName,
  readOnly,
  grayedOut,
  isPending,
  resourceOptions = EMPTY_YAML_FORM_RESOURCE_OPTIONS,
}: Props) => {
  const schema: JsonSchema | undefined = isJsonSchema(configSchema)
    ? configSchema
    : undefined;
  const canUseForm = Boolean(schema && testName) && !readOnly;
  const [userMode, setUserMode] = useState<EditorMode | null>(null);
  const [modeTestName, setModeTestName] = useState(testName);
  const [rawYaml, setRawYaml] = useState(value);
  const [formValues, setFormValues] = useState<YamlFormValues>(() =>
    testName ? parseYamlFormValues(value, testName) ?? {} : {},
  );
  const lastEmittedRef = useRef<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  if (modeTestName !== testName) {
    setModeTestName(testName);
    setUserMode(null);
  }

  const mode: EditorMode = userMode ?? (canUseForm ? 'form' : 'yaml');

  const fields = useMemo(
    () => (schema ? schemaToFormFields(schema) : []),
    [schema],
  );

  const wrappedSchema = useMemo(
    () => getSchema(testName, schema),
    [testName, schema],
  );

  const validator = useMemo(() => {
    if (!wrappedSchema) {
      return undefined;
    }
    return ajv.compile(wrappedSchema);
  }, [wrappedSchema]);

  const validationErrors = useMemo(
    () => validateYaml(rawYaml, validator),
    [rawYaml, validator],
  );

  const formValuesToShow = useMemo(() => {
    if (!testName) {
      return formValues;
    }
    const yamlToParse = rawYaml.trim() ? rawYaml : value;
    return parseYamlFormValues(yamlToParse, testName) ?? formValues;
  }, [formValues, rawYaml, testName, value]);

  const emitYaml = (raw: string) => {
    const stripped = removeLineComments(raw);
    lastEmittedRef.current = stripped;
    onChangeRef.current(stripped, validateYaml(raw, validator).length === 0);
  };

  const reportValidity = (yamlValue: string) => {
    lastEmittedRef.current = yamlValue;
    onChangeRef.current(
      yamlValue,
      validateYaml(yamlValue, validator).length === 0,
    );
  };

  useEffect(() => {
    if (value === lastEmittedRef.current) {
      return;
    }
    setRawYaml(value);
    if (testName) {
      const parsed = parseYamlFormValues(value, testName);
      if (parsed) {
        setFormValues(parsed);
      }
    }
    reportValidity(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, testName, canUseForm]);

  useEffect(() => {
    const current = lastEmittedRef.current ?? rawYaml;
    onChangeRef.current(current, validateYaml(rawYaml, validator).length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validator]);

  const handleModeChange = (_: unknown, next: EditorMode | null) => {
    if (!next) {
      return;
    }
    if (next === 'form') {
      if (!testName) {
        return;
      }
      const parsed = parseYamlFormValues(rawYaml, testName);
      if (!parsed) {
        return;
      }
      setFormValues(parsed);
      setUserMode('form');
      return;
    }
    setUserMode('yaml');
  };

  const handleCodeChange = (next: string) => {
    setRawYaml(next);
    emitYaml(next);
  };

  const handleFormChange = (next: YamlFormValues) => {
    setFormValues(next);
    if (!testName || !schema) {
      return;
    }
    const dumped = dumpYamlFormValues(next, testName, schema);
    setRawYaml(dumped);
    emitYaml(dumped);
  };

  return (
    <EditorRoot>
      {canUseForm && (
        <ToggleRow>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="form">Form</ToggleButton>
            <ToggleButton value="yaml">YAML</ToggleButton>
          </ToggleButtonGroup>
        </ToggleRow>
      )}
      {isPending ? null : mode === 'form' && canUseForm ? (
        <FlexColumn sx={{ flex: 1, gap: 2, minHeight: 0 }}>
          <YamlForm
            fields={fields}
            values={formValuesToShow}
            onChange={handleFormChange}
            resourceOptions={resourceOptions}
          />
          {validationErrors.length > 0 && (
            <Alert severity="error">
              {validationErrors.map((error, index) => (
                <Typography key={index}>{error.message}</Typography>
              ))}
            </Alert>
          )}
        </FlexColumn>
      ) : (
        <YamlCodeEditor
          editorKey={testName}
          value={rawYaml}
          onChange={handleCodeChange}
          readOnly={readOnly}
          grayedOut={grayedOut}
          errors={validationErrors}
        />
      )}
    </EditorRoot>
  );
};

export default YamlEditor;
